import { successesOf, throwLine, throwOne } from './staged.js';
import { testDiceData } from './test.js';
import { HOST_LIMITS } from './matrixHost.js';
const KEY_RE = /^[a-z][a-z0-9_]{0,62}$/;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isKey = (v) => typeof v === 'string' && KEY_RE.test(v);
const field = (v) => (isObj(v) && isKey(v.purpose) && isKey(v.which) && typeof v.label === 'string' && v.label.trim().length > 0 && v.label.length <= 40 ? { purpose: v.purpose, which: v.which, label: v.label.trim() } : null);
/** `matrix.cyber` strictly, its codes the system's own; null when any part is malformed. */
export function parseCyber(v, codeKeys) {
    if (!isObj(v) || Object.keys(v).some((k) => !['codes', 'monitor', 'stage_every', 'crash_adds_rating', 'kinds', 'fields'].includes(k)))
        return null;
    if (!isObj(v.codes) || !isInt(v.monitor, 1, 40) || !isInt(v.stage_every, 1, 10) || typeof v.crash_adds_rating !== 'boolean')
        return null;
    const codes = {};
    for (const k of codeKeys) {
        const c = v.codes[k];
        if (!isObj(c) || !isInt(c.tn, 1, 30) || !isKey(c.ic_damage) || !isKey(c.dump_shock))
            return null;
        codes[k] = { tn: c.tn, icDamage: c.ic_damage, dumpShock: c.dump_shock };
    }
    if (Object.keys(v.codes).length !== codeKeys.length)
        return null;
    const kinds = v.kinds;
    if (!isObj(kinds) || !isKey(kinds.stun) || !isKey(kinds.physical))
        return null;
    const f = v.fields;
    if (!isObj(f) || !isObj(f.persona))
        return null;
    const persona = {};
    for (const [k, x] of Object.entries(f.persona)) {
        const got = field(x);
        if (!isKey(k) || !got)
            return null;
        persona[k] = got;
    }
    const armor = field(f.armor);
    const mpcp = field(f.mpcp);
    const hardening = field(f.hardening);
    const body = field(f.body);
    const willpower = field(f.willpower);
    if (!armor || !mpcp || !hardening || !body || !willpower || !Object.keys(persona).length)
        return null;
    return { codes, monitor: v.monitor, stagePer: v.stage_every, crashAddsRating: v.crash_adds_rating, kinds: { stun: kinds.stun, physical: kinds.physical }, fields: { persona, armor, mpcp, hardening, body, willpower } };
}
// ── The request ───────────────────────────────────────────────────────────
export const CYBER_LIMITS = { name: 60, rating: 20, pool: 30 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// eslint-disable-next-line no-control-regex
const LINE_BAD = /[\u0000-\u001f\u007f]/;
/** A cybercombat request off the wire: exact shape or null. Untrusted input, always. */
export function parseCyberRequest(v) {
    if (!isObj(v) || v.kind !== 'matrix.cyber' || !UUID_RE.test(String(v.decker)) || !UUID_RE.test(String(v.ic)))
        return null;
    if (v.act === 'ic')
        return Object.keys(v).every((k) => ['kind', 'act', 'decker', 'ic'].includes(k)) ? { kind: 'matrix.cyber', act: 'ic', decker: String(v.decker), ic: String(v.ic) } : null;
    if (v.act !== 'attack' || Object.keys(v).some((k) => !['kind', 'act', 'decker', 'ic', 'utility', 'pool'].includes(k)))
        return null;
    const u = v.utility;
    if (!isObj(u) || Object.keys(u).some((k) => !['name', 'rating', 'level'].includes(k)) || typeof u.name !== 'string' || !u.name.trim() || u.name.length > CYBER_LIMITS.name || LINE_BAD.test(u.name))
        return null;
    if (!isInt(u.rating, 1, CYBER_LIMITS.rating) || !isKey(u.level) || !isInt(v.pool ?? 0, 0, CYBER_LIMITS.pool))
        return null;
    return { kind: 'matrix.cyber', act: 'attack', decker: String(v.decker), ic: String(v.ic), utility: { name: u.name.trim(), rating: u.rating, level: u.level }, pool: v.pool ?? 0 };
}
export function icPlayOf(attrs, data) {
    const r = isObj(data) ? data : {};
    const out = { roll: typeof attrs.roll === 'string' ? attrs.roll : '', armor: attrs.armor_applies === true };
    const lo = r.lower;
    if (isObj(lo) && isKey(lo.attribute) && isInt(lo.per_net_successes, 1, 10) && isInt(lo.floor, 0, 10))
        out.lower = { attribute: lo.attribute, per: lo.per_net_successes, floor: lo.floor };
    const m = r.mpcp_test;
    if (isObj(m) && isKey(m.when) && isInt(m.tn_add ?? 0, -10, 10))
        out.mpcp = { when: m.when, tnAdd: m.tn_add ?? 0, per: isInt(m.per_successes, 1, 10) ? m.per_successes : 1, lost: isInt(m.mpcp_lost, 1, 10) ? m.mpcp_lost : 1, hardening: m.hardening === 'adds_to_tn' };
    const d = r.decker_damage;
    if (isObj(d) && isKey(d.when) && isKey(d.level))
        out.deckerDamage = { when: d.when, level: d.level, stun: d.stun === true, hardening: d.hardening === 'reduces_power' };
    const b = r.decker;
    if (isObj(b) && (b.resist === 'body' || b.resist === 'willpower'))
        out.black = { resist: b.resist, stun: b.stun === true, hardening: isObj(r.damage) && r.damage.hardening === 'reduces_power', jackOut: isObj(r.jack_out) };
    return out;
}
// ── Working ───────────────────────────────────────────────────────────────
/** A level staged by net successes, `per` a step: up when positive, down when negative; null below the lowest. */
export function stageLevel(levels, start, net, per) {
    const i = levels.findIndex((l) => l.key === start);
    if (i < 0)
        return null;
    const at = i + Math.trunc(net / per);
    return at < 0 ? null : levels[Math.min(levels.length - 1, at)];
}
const icName = (ic) => `${ic.name}-${ic.rating}`;
const nextState = (s, ic, patch, rest = {}) => ({
    ...s, host: { ...s.host, ratings: { ...s.host.ratings }, ...(s.host.sheaf ? { sheaf: s.host.sheaf.map((x) => ({ ...x })) } : {}) },
    ...(s.fired ? { fired: [...s.fired] } : {}), ...(s.lowered ? { lowered: { ...s.lowered } } : {}),
    ic: (s.ic ?? []).map((x) => (x.combatant === ic.combatant ? { ...x, ...patch } : { ...x })),
    ...rest,
});
/** The decker's attack on an IC (p. 222-226): a string is a refusal in words. */
export function deckerAttack(req, m, rng) {
    const code = m.rules.codes[m.state.host.code];
    if (!code)
        return "The host's security code is not one of this game system's.";
    if (!m.levels.some((l) => l.key === req.utility.level))
        return "That is not one of this game system's damage levels.";
    const data = testDiceData(m.dice);
    const atk = throwOne(m.dice, { label: req.utility.name, rating: req.utility.rating, spent: null, dice: req.utility.rating + req.pool, target: code.tn, base: 'security code', terms: [] }, rng);
    if (typeof atk === 'string')
        return atk;
    const res = throwOne(m.dice, { label: 'Security Value', rating: m.state.host.security, spent: null, dice: m.state.host.security, target: req.utility.rating, base: 'Power', terms: [] }, rng);
    if (typeof res === 'string')
        return res;
    const a = successesOf(data, atk);
    const r = successesOf(data, res);
    const level = a > 0 ? stageLevel(m.levels, req.utility.level, a - r, m.rules.stagePer) : null;
    const before = m.ic.damage ?? 0;
    const damage = Math.min(m.rules.monitor, before + (level?.boxes ?? 0));
    const crashed = damage >= m.rules.monitor;
    const tally = crashed && m.rules.crashAddsRating ? Math.min(HOST_LIMITS.tally, m.state.tally + m.ic.rating) : m.state.tally;
    const state = crashed
        ? { ...nextState(m.state, m.ic, {}), ic: (m.state.ic ?? []).filter((x) => x.combatant !== m.ic.combatant).map((x) => ({ ...x })), tally }
        : nextState(m.state, m.ic, { damage, met: true });
    const what = !level ? 'no damage' : `${level.label} (${level.boxes}), ${damage}/${m.rules.monitor}${crashed ? ': crashed' : ''}`;
    const line = `${m.deckerName} attacks ${icName(m.ic)}. ${throwLine(req.utility.name, data, atk)}; ${throwLine(icName(m.ic), data, res)}: ${what}${crashed && m.rules.crashAddsRating ? `; tally ${tally}` : ''}.`;
    const told = `${req.utility.name} against ${icName(m.ic)}: ${a} successes to its ${r}: ${what}.`;
    return { state, line, told, ...(crashed ? { crashed: true } : {}), hurt: [] };
}
/** One test of the decker's or the IC's, thrown: its working and successes. */
function test(m, label, dice, target, base, rng) {
    const t = throwOne(m.dice, { label, rating: dice, spent: null, dice: Math.max(0, dice), target: Math.max(2, target), base, terms: [] }, rng);
    return typeof t === 'string' ? t : { t, n: successesOf(testDiceData(m.dice), t) };
}
/** An IC acting against the decker, by its entry (p. 227-230). A string is a refusal in words. */
export function icActs(play, m, rng) {
    const code = m.rules.codes[m.state.host.code];
    if (!code)
        return "The host's security code is not one of this game system's.";
    const F = m.rules.fields;
    const data = testDiceData(m.dice);
    const name = icName(m.ic);
    const words = [];
    const hurt = [];
    let mpcpLost = 0;
    let persona = m.state.persona ?? 0;
    const lowered = { ...(m.state.lowered ?? {}) };
    const sheetOr0 = (f) => m.sheet(f) ?? 0;
    const mpcp = () => Math.max(0, sheetOr0(F.mpcp) - (m.state.mpcpLost ?? 0) - mpcpLost);
    const mpcpTest = (when) => {
        if (!play.mpcp || play.mpcp.when !== when)
            return 0;
        const got = test(m, name, m.ic.rating, mpcp() + play.mpcp.tnAdd + (play.mpcp.hardening ? sheetOr0(F.hardening) : 0), F.mpcp.label, rng);
        if (typeof got === 'string')
            return got;
        const lost = Math.min(mpcp(), Math.floor(got.n / play.mpcp.per) * play.mpcp.lost);
        mpcpLost += lost;
        words.push(`${throwLine(`${name} against ${F.mpcp.label}`, data, got.t)}: ${lost ? `${lost} ${F.mpcp.label} burned out` : 'none burned out'}`);
        return got.n;
    };
    const deckerHit = (label, power, start, net, resist, stun, hardening) => {
        const got = test(m, resist.label, sheetOr0(resist), power - (hardening ? sheetOr0(F.hardening) : 0), 'Power', rng);
        if (typeof got === 'string')
            return got;
        const lv = stageLevel(m.levels, start, net - got.n, m.rules.stagePer);
        words.push(`${throwLine(`${m.deckerName}'s ${resist.label}`, data, got.t)}: ${lv ? `${lv.label} ${stun ? 'Stun' : 'Physical'} (${lv.boxes})` : 'no damage'} from ${label}`);
        if (lv)
            hurt.push({ kind: stun ? m.rules.kinds.stun : m.rules.kinds.physical, boxes: lv.boxes });
        return null;
    };
    let dumped = false;
    if (play.roll === 'damage' || play.roll === 'crippler' || play.roll === 'black') {
        const atk = test(m, name, m.state.host.security, code.tn, 'security code', rng);
        if (typeof atk === 'string')
            return atk;
        words.push(throwLine(`${name} attacks`, data, atk.t));
        if (atk.n > 0 && play.roll === 'damage') {
            const res = test(m, F.persona.bod?.label ?? 'Bod', Math.max(0, (m.sheet(F.persona.bod ?? F.armor) ?? 0) - (lowered.bod ?? 0)), m.ic.rating - (play.armor ? sheetOr0(F.armor) : 0), 'Power', rng);
            if (typeof res === 'string')
                return res;
            const lv = stageLevel(m.levels, code.icDamage, atk.n - res.n, m.rules.stagePer);
            persona = Math.min(m.rules.monitor, persona + (lv?.boxes ?? 0));
            words.push(`${throwLine('Persona resists', data, res.t)}: ${lv ? `${lv.label} (${lv.boxes}), persona ${persona}/${m.rules.monitor}` : 'no damage'}`);
            if (persona >= m.rules.monitor) {
                dumped = true;
                words.push('the persona crashes: the decker is dumped');
                const crashN = mpcpTest('crashes_persona');
                if (typeof crashN === 'string')
                    return crashN;
                if (play.deckerDamage?.when === 'crashes_persona') {
                    const e = deckerHit(name, m.ic.rating, play.deckerDamage.level, crashN, F.body, play.deckerDamage.stun, play.deckerDamage.hardening);
                    if (e)
                        return e;
                }
                const dumpN = mpcpTest('dumps_decker');
                if (typeof dumpN === 'string')
                    return dumpN;
            }
        }
        else if (atk.n > 0 && play.roll === 'crippler' && play.lower) {
            const attr = play.lower.attribute;
            const f = F.persona[attr];
            if (!f)
                return `This game system's data names no persona ${attr} on the sheet.`;
            const have = Math.max(0, (m.sheet(f) ?? 0) - (lowered[attr] ?? 0));
            const res = test(m, f.label, have, m.ic.rating, 'rating', rng);
            if (typeof res === 'string')
                return res;
            const down = Math.max(0, Math.min(have - play.lower.floor, Math.floor((atk.n - res.n) / play.lower.per)));
            if (down)
                lowered[attr] = (lowered[attr] ?? 0) + down;
            words.push(`${throwLine(`${f.label} resists`, data, res.t)}: ${down ? `${f.label} ${have - down}` : 'no effect'}`);
            if (down && have - down === 0) {
                const z = mpcpTest('attribute_zero');
                if (typeof z === 'string')
                    return z;
            }
        }
        else if (atk.n > 0 && play.roll === 'black' && play.black) {
            const e = deckerHit(name, m.ic.rating, code.icDamage, atk.n, play.black.resist === 'body' ? F.body : F.willpower, play.black.stun, play.black.hardening);
            if (e)
                return e;
        }
    }
    else {
        return `${name} does not attack: it acts on a System Test or a utility, which the DM rules.`;
    }
    // Dump shock (p. 226): Stun, Power the Security Value, its level by the code, resisted with Willpower.
    if (dumped) {
        const e = deckerHit('dump shock', m.state.host.security, code.dumpShock, 0, F.willpower, true, false);
        if (e)
            return e;
    }
    const blackHit = play.roll === 'black' && hurt.length > 0 && play.black?.jackOut;
    const state = nextState(m.state, m.ic, { met: true, ...(blackHit ? { holds: true } : {}) }, {
        ...(dumped ? { persona: 0 } : persona ? { persona } : {}),
        ...(Object.keys(lowered).length ? { lowered } : {}),
        ...((m.state.mpcpLost ?? 0) + mpcpLost ? { mpcpLost: (m.state.mpcpLost ?? 0) + mpcpLost } : {}),
    });
    if (dumped)
        delete state.persona;
    return { state, line: `${name} acts on ${m.deckerName}. ${words.join('; ')}.`, ...(dumped ? { dumped: true } : {}), hurt, ...(mpcpLost ? { mpcpLost } : {}) };
}
/** Jacking out under Black IC that has hit (p. 230): Willpower against the highest such IC's rating; a string, refused. */
export function blackHolds(m, rng) {
    const holding = (m.state.ic ?? []).filter((x) => x.holds);
    if (!holding.length)
        return null;
    const top = holding.reduce((a, b) => (b.rating > a.rating ? b : a));
    const got = test({ ...m, ic: top }, m.rules.fields.willpower.label, m.sheet(m.rules.fields.willpower) ?? 0, top.rating, 'rating', rng);
    if (typeof got === 'string')
        return { line: got, free: false };
    return { line: `${m.deckerName} tries to jack out under ${icName(top)}: ${throwLine(m.rules.fields.willpower.label, testDiceData(m.dice), got.t)}: ${got.n ? 'free' : 'held'}.`, free: got.n > 0 };
}
//# sourceMappingURL=cyber.js.map